import React from 'react';
import { Check, CheckCircle2, MapIcon } from 'lucide-react';
// import { basemaps } from '../../hooks/useMapInstance';
import { useMap } from '@/providers/MapProvider';
import TileLayer from 'ol/layer/Tile';
import { Google, OSM, XYZ } from 'ol/source';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import Control from 'ol/control/Control';
import { defaults as defaultControls } from 'ol/control/defaults.js';
import { BasemapType, useMapStore } from '@/stores/mapStore';

const googleAPIKey = import.meta.env.VITE_GOOGLE_API_KEY;

export const basemaps = {
  osm: new TileLayer({
    source: new OSM({}),
    preload: Infinity,
  }),
  satellite: new TileLayer({
    source: new XYZ({
      url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      attributions: 'Tiles © Esri',
    }),
    preload: Infinity,
  }),
  googleRoadmap: new TileLayer({
    source: new Google({
      key: googleAPIKey,
      mapType: 'roadmap',
    }),
    preload: Infinity,
  }),
  googleSatellite: new TileLayer({
    source: new Google({
      key: googleAPIKey,
      mapType: 'satellite',
    }),
    preload: Infinity,
  }),
};

export const createGoogleLogoControl = () => {
  const element = document.createElement('div');

  element.className =
    'ol-google-logo hidden pointer-events-none absolute bottom-1 left-1 z-100';

  const image = document.createElement('img');
  image.src =
    'https://maps.gstatic.com/mapfiles/api-3/images/google_white5.png';
  image.alt = 'Google';
  image.className = 'h-6 w-auto';

  element.appendChild(image);

  return new Control({ element });
};

export const BasemapPanel = () => {
  const { map } = useMap();
  const { activeBasemap, setActiveBasemap } = useMapStore();

  const handleBasemapChange = (type: BasemapType) => {
    if (!map) return;
    map.getLayers().setAt(0, basemaps[type]);
    setActiveBasemap(type);
  };

  return (
    <div className="flex flex-col">
      <BasemapOption
        label="Google Roadmap"
        imgSrc="/basemaps/google-roadmap.png"
        isSelected={activeBasemap === 'googleRoadmap'}
        onClick={() => handleBasemapChange('googleRoadmap')}
      />
      <BasemapOption
        label="Google Satellite"
        imgSrc="/basemaps/google-satellite.png"
        isSelected={activeBasemap === 'googleSatellite'}
        onClick={() => handleBasemapChange('googleSatellite')}
      />
      <BasemapOption
        label="ESRI Satellite"
        imgSrc="/basemaps/esri-satellite.png"
        isSelected={activeBasemap === 'satellite'}
        onClick={() => handleBasemapChange('satellite')}
      />
      <BasemapOption
        label="OSM Street"
        imgSrc="/basemaps/osm-street.png"
        isSelected={activeBasemap === 'osm'}
        onClick={() => handleBasemapChange('osm')}
      />
    </div>
  );
};

interface BasemapOptionProps {
  label: string;
  imgSrc?: string;
  isSelected: boolean;
  onClick: () => void;
}

export const BasemapOption: React.FC<BasemapOptionProps> = ({
  label,
  imgSrc,
  isSelected,
  onClick,
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        'group flex w-full items-center gap-3 rounded-md border p-2 text-left transition-all hover:bg-accent cursor-pointer',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-transparent hover:border-border',
      )}
    >
      <div
        className={cn(
          'relative h-10 w-10 shrink-0 overflow-hidden rounded-md border shadow-sm',
          isSelected ? 'border-primary ring-1 ring-primary' : 'border-border',
        )}
      >
        {imgSrc ? (
          <img
            src={imgSrc}
            alt={label}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
            <MapIcon className="h-4 w-4 opacity-50" />
          </div>
        )}
      </div>

      <span
        className={cn(
          'flex-1 text-sm font-medium',
          isSelected ? 'text-primary' : 'text-foreground',
        )}
      >
        {label}
      </span>

      {isSelected && (
        <Check className="h-4 w-4 text-primary" strokeWidth={2.5} />
      )}
    </button>
  );
};
